import { TarjetaConsultoria } from "../components/TarjetaConsultoria";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ResumenGeneral } from "../components/ResumenGeneral";
import { ClasesStockVenta } from "../components/ClasesStockVenta";
import { InventarioEvolucion } from "../components/InventarioEvolucion";
import { RankingLabsDParado } from "../components/RankingLabsDParado";
import { KpiCompradoSinVenta } from "../components/KpiCompradoSinVenta";
import { StockDestacados } from "../components/indicadores/StockDestacados";
import { InfoD } from "../components/InfoD";
import { BannerFarmanaliza } from "../components/BannerFarmanaliza";
import { useAuth } from "../lib/auth";
import api, { API, formatMoney, formatNumber, formatPct, ABCD_ORDER, ABCD_COLORS, ABCD_LABELS } from "../lib/api";
import { Plus, FileSpreadsheet, TrendingUp, AlertTriangle, Package, Loader2, AlertCircle, CheckCircle2, ArrowDown, ArrowUp, Send, Sparkles, Zap, Wrench } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { useSortable, SortHeader } from "../lib/sort";
import { ExportButtons } from "../lib/exports";
import { col } from "../lib/exportCols";
import DrillDown from "../components/DrillDown";

function PeriodFixBanner({ study, onFixed }) {
  const [custom, setCustom] = useState(study?.period_days || 365);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const quickVals = [30, 60, 90, 180, 365, 730];
  const healedFrom = study.period_auto_healed_from;
  const isTooShort = study.period_days < 30;
  const isTooLong = study.period_days > 730;

  const fix = async (days) => {
    setBusy(true); setErr("");
    try {
      await api.patch(`/studies/${study.id}/fix-period`, { period_days: Number(days) });
      onFixed?.();
    } catch (e) {
      const st = e.response?.status;
      const d = e.response?.data?.detail;
      setErr(d || (st ? `Error ${st} al corregir el periodo${st === 404 ? " (el servidor no tiene esta función: vuelve a publicar la app)" : ""}` : "No se pudo contactar con el servidor"));
    } finally { setBusy(false); }
  };

  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/studies/${study.id}`);
      window.location.href = "/";
    } finally { setBusy(false); }
  };

  return (
    <div className="card mb-6 border-2 border-amber-400 bg-amber-50/60 overflow-hidden" data-testid="period-fix-banner">
      <div className="px-5 py-4 flex items-start gap-3">
        <AlertTriangle size={24} className="text-amber-600 shrink-0 mt-1"/>
        <div className="flex-1">
          <h3 className="font-display font-bold text-base text-amber-900">
            {healedFrom
              ? <>Periodo corregido automáticamente: {healedFrom} → {study.period_days} días</>
              : <>Periodo del estudio sospechoso: {study.period_days} día{study.period_days !== 1 ? "s" : ""}</>}
          </h3>
          <p className="text-sm text-amber-900 mt-1 max-w-3xl">
            {healedFrom && <>El estudio tenía un periodo imposible de <b>{healedFrom} día(s)</b>, lo que vaciaba las clases intermedias (C, CC, B). Lo hemos recalculado con <b>{study.period_days} días</b> (periodo detectado en el fichero). <b>Si tu Excel abarca otro periodo, ajústalo aquí</b> y volveremos a clasificar al instante.</>}            {isTooShort && <>Con solo <b>{study.period_days} día(s)</b> de ventas la clasificación ABCD no es fiable (las clases intermedias como C, CC quedan vacías o distorsionadas).</>}
            {isTooLong && <>Un periodo de <b>{study.period_days} días</b> (~{Math.round(study.period_days/365)} años) es excesivo y aplana la clasificación.</>}
            {!healedFrom && <>{" "}Ajusta el periodo real del Excel que subiste (p. ej. <b>365</b> si es un año) y recalcularemos la clasificación al instante.</>}
          </p>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {quickVals.map((d) => (
              <button key={d} onClick={() => fix(d)} disabled={busy}
                      className="px-3 py-1.5 rounded-lg text-xs font-bold border border-amber-300 bg-white hover:bg-amber-100 text-amber-900 disabled:opacity-50"
                      data-testid={`fix-period-${d}`}>
                {d}d
              </button>
            ))}
            <span className="text-xs text-amber-900">o</span>
            <input type="number" min="7" max="730" value={custom}
                   onChange={(e) => setCustom(e.target.value)}
                   className="input text-sm w-24" data-testid="fix-period-custom"/>
            <button onClick={() => fix(custom)} disabled={busy}
                    className="btn btn-primary btn-sm" data-testid="fix-period-apply">
              {busy ? <><Loader2 size={12} className="animate-spin"/> Recalculando…</> : <><Wrench size={12}/> Aplicar</>}
            </button>
            <div className="flex-1"/>
            {!deleteConfirm ? (
              <button onClick={() => setDeleteConfirm(true)} className="text-xs text-red-700 hover:underline" data-testid="delete-study-open">
                O eliminar este estudio
              </button>
            ) : (
              <div className="flex items-center gap-2 text-xs">
                <span className="font-bold text-red-700">¿Seguro? Se borrarán {formatNumber(study.total_products)} productos</span>
                <button onClick={del} disabled={busy} className="px-2 py-1 rounded bg-red-600 text-white font-bold" data-testid="delete-study-confirm">Sí, borrar</button>
                <button onClick={() => setDeleteConfirm(false)} className="px-2 py-1 rounded bg-slate-200 font-semibold">Cancelar</button>
              </div>
            )}
          </div>
          {err && <div className="mt-2 text-xs text-red-700">{err}</div>}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [studies, setStudies] = useState([]);
  const [latest, setLatest] = useState(null);
  const [benchmark, setBenchmark] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/studies");
        setStudies(data);
        if (data[0]) {
          const detail = await api.get(`/studies/${data[0].id}`);
          setLatest(detail.data);
        }
        const bench = await api.get("/benchmark/abcd");
        setBenchmark(bench.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="p-12 flex items-center gap-2 text-slate-500">
        <Loader2 className="animate-spin" size={18} /> Cargando…
      </div>
    );
  }

  if (studies.length === 0) {
    return (
      <div className="p-12" data-testid="dashboard-empty">
        <div className="max-w-2xl">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Bienvenido</div>
          <h1 className="font-display text-4xl font-extrabold mt-2 tracking-tight">Empieza tu primer estudio</h1>
          <p className="text-slate-600 mt-3">Sube el listado Excel de tu farmacia y obtendrás de forma automática la clasificación ABCD, análisis por laboratorio, detección de stock muerto y mucho más.</p>
          <Link to="/upload" data-testid="upload-cta-empty" className="btn btn-primary mt-6">
            <Plus size={16} /> Subir mi primer estudio
          </Link>
        </div>
        <div className="mt-10">
          <ResumenGeneral />
        </div>
      </div>
    );
  }

  const t = latest?.totals || {};
  const abcdData = ABCD_ORDER.map((k) => ({
    code: k,
    label: ABCD_LABELS[k],
    count: latest?.abcd_counts?.[k] || 0,
    stock: latest?.abcd_totals?.[k]?.stock_value || 0,
    sales: latest?.abcd_totals?.[k]?.sales_value || 0,
  }));

  const benchData = benchmark && benchmark.n_studies > 0
    ? ABCD_ORDER.map((k) => ({
        code: k,
        tu: latest?.total_products ? ((latest.abcd_counts[k] || 0) / latest.total_products) * 100 : 0,
        media: benchmark.abcd_pct_count[k] || 0,
      }))
    : null;

  const dMuerto = latest?.stock_d_subdivision?.d_muerto || { count: 0, stock_value: 0 };
  const goStockMuerto = () => navigate(`/studies/${latest.id}?tab=stockd`);
  const goAbcdVentas = (code) => navigate(`/studies/${latest.id}?tab=ventas${code ? `&abcd=${code}` : ""}`);
  const goAbcdResumen = (code) => navigate(`/studies/${latest.id}?tab=resumen${code ? `&abcd=${code}` : ""}`);
  const goAbcdStock = () => navigate(`/studies/${latest.id}?tab=stock`);

  return (
    <div className="p-8 fade-in" data-testid="dashboard">
      <BannerFarmanaliza image="/img/farmanaliza-cuadro.jpg?v=2" kicker="Cuadro de mando" pharmacy={user?.pharmacy_name} studyId={latest?.id} />
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Cuadro de mando · Último estudio</div>
          <h1 className="font-display text-4xl font-extrabold mt-1 tracking-tight">{latest?.name}</h1>
          <p className="text-slate-600 mt-1 text-sm">
            Periodo: <span className="font-mono font-semibold">{latest?.period_days} días</span>
            {" · "}{formatNumber(latest?.total_products)} productos
            {" · "}Subido el {new Date(latest?.created_at).toLocaleDateString("es-ES")}
          </p>
        </div>
        <Link to={`/studies/${latest?.id}`} className="btn btn-outline" data-testid="view-latest-study">
          Ver análisis completo →
        </Link>
      </div>

      {/* Aviso de periodo sospechoso o auto-corregido → ofrece ajustarlo */}
      {latest && (latest.period_days < 30 || latest.period_days > 730 || latest.period_auto_healed_from) && (
        <PeriodFixBanner study={latest} onFixed={() => window.location.reload()} />
      )}

      {/* Alertas inteligentes (global) */}
      <AlertsBanner latest={latest} />

      {/* Indicadores destacados → mega cuadro de indicadores de stock */}
      <StockDestacados studyId={latest?.id} />

      <ResumenGeneral isAdmin={user?.role === "admin"} />

      {/* Charts row (global) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div
          className="card p-6 lg:col-span-2 cursor-pointer hover:shadow-md hover:border-slate-300 transition"
          data-testid="chart-abcd-bars"
          onClick={() => goAbcdResumen()}
          role="button"
          tabIndex={0}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Distribución ABCD · Global</div>
              <h3 className="font-display text-lg font-bold mt-1">Productos por clasificación</h3>
            </div>
            <span className="text-xs font-semibold text-slate-500">Click para desglosar →</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={abcdData} margin={{ top: 5, right: 0, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="code" stroke="#94A3B8" tick={{ fontSize: 12, fontWeight: 600 }} />
              <YAxis stroke="#94A3B8" tick={{ fontSize: 11 }} />
              <Tooltip
                cursor={{ fill: "#F1F5F9" }}
                formatter={(v) => [formatNumber(v), "Productos"]}
                labelFormatter={(c) => `${c} - ${ABCD_LABELS[c]}`}
                contentStyle={{ borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }}
              />
              <Bar
                dataKey="count"
                radius={[6, 6, 0, 0]}
                onClick={(d) => { if (d?.code) goAbcdResumen(d.code); }}
                style={{ cursor: "pointer" }}
              >
                {abcdData.map((d) => (<Cell key={d.code} fill={ABCD_COLORS[d.code]} />))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div
          className="card p-6 cursor-pointer hover:shadow-md hover:border-slate-300 transition"
          data-testid="chart-abcd-donut"
          onClick={() => goAbcdVentas()}
          role="button"
          tabIndex={0}
        >
          <div className="flex items-baseline justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">% Ventas · Global</div>
              <h3 className="font-display text-lg font-bold mt-1">Por clasificación</h3>
            </div>
            <span className="text-xs font-semibold text-slate-500">Ver →</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={abcdData}
                dataKey="sales"
                nameKey="code"
                innerRadius={50}
                outerRadius={90}
                paddingAngle={2}
                onClick={(d) => { if (d?.code) goAbcdVentas(d.code); }}
                style={{ cursor: "pointer" }}
              >
                {abcdData.map((d) => (<Cell key={d.code} fill={ABCD_COLORS[d.code]} />))}
              </Pie>
              <Tooltip formatter={(v) => formatMoney(v)} contentStyle={{ borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} />
              <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <ClasesStockVenta data={abcdData} colors={ABCD_COLORS} onClick={(c) => goAbcdVentas(c)} />

      <InventarioEvolucion />

      <RankingLabsDParado studyId={latest.id} />

      {/* ===== Segmentación FARMACIA / PARAFARMACIA ===== */}
      <SegmentedView studyId={latest.id} />


      {/* Benchmark */}
      {benchData && (
        <div className="card p-6" data-testid="benchmark-card">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Benchmark anónimo</div>
              <h3 className="font-display text-lg font-bold mt-1">Tu farmacia vs media del resto ({benchmark.n_studies} estudios)</h3>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={benchData} margin={{ top: 5, right: 0, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="code" stroke="#94A3B8" tick={{ fontSize: 12, fontWeight: 600 }} />
              <YAxis stroke="#94A3B8" tick={{ fontSize: 11 }} unit="%" />
              <Tooltip formatter={(v) => `${(v || 0).toFixed(1)}%`} contentStyle={{ borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="tu" name="Tú" fill="#0F172A" radius={[4, 4, 0, 0]} />
              <Bar dataKey="media" name="Media" fill="#94A3B8" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Cobertura de familias (Parafarmacia) — labs con ventas anuales ≥ 2.000€ */}
      <FamilyCoveragePanel studyId={latest.id} />

      {/* Studies list mini */}
      {studies.length > 1 && <RecentStudiesTable studies={studies} />}

      <TarjetaConsultoria />
    </div>
  );
}

function FamilyCoveragePanel({ studyId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [famDrill, setFamDrill] = useState(null);
  useEffect(() => {
    if (!studyId) return;
    (async () => {
      try {
        const { data: d } = await api.get(`/studies/${studyId}/family-coverage`, {
          params: { segment: "parafarmacia" }
        });
        setData(d);
      } finally { setLoading(false); }
    })();
  }, [studyId]);

  const sort = useSortable(data?.families || [], "labs_qualified", "desc");

  if (loading) return <div className="card p-6 mt-8 text-slate-500"><Loader2 size={14} className="animate-spin inline mr-2" />Cargando cobertura de familias…</div>;
  if (!data) return null;

  const open = (fam) => setFamDrill(fam);

  return (
    <div className="mt-8" data-testid="family-coverage-panel">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Parafarmacia · Cobertura por familia</div>
          <h3 className="font-display text-xl font-bold mt-1">Laboratorios con ventas anuales ≥ {formatMoney(data.threshold_annual)}</h3>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          <div className="text-xs text-slate-500 italic max-w-md text-right">
            Click en una familia → análisis comparativo entre los laboratorios significativos de esa familia.
          </div>
          <ExportButtons
            filename="cobertura-familias-parafarmacia"
            title="Cobertura por familia · Parafarmacia"
            subtitle={`Umbral ventas anuales ≥ ${formatMoney(data.threshold_annual)}`}
            sheetName="Cobertura"
            columns={[
              { header: "Familia", key: "familia" },
              { header: "Labs ≥ 2.000€", key: "labs_qualified", align: "right" },
              { header: "Labs totales", key: "labs_total", align: "right" },
              { header: "Productos", key: "products", align: "right" },
              { header: "Ventas anuales (€)", key: "sales_annual", format: (v) => (v || 0).toFixed(2), align: "right" },
              { header: "V/S", key: "ratio_vs", format: (v) => (v || 0).toFixed(2), align: "right" },
            ]}
            rows={sort.sortedRows}
          />
        </div>
      </div>
      <div className="card overflow-x-auto">
        <table className="dense" data-testid="family-coverage-table">
          <thead>
            <tr>
              <th>#</th>
              <th><SortHeader label="Familia" k="familia" sort={sort} defaultDir="asc" /></th>
              <th className="num"><SortHeader label="Labs con ventas ≥ 2.000€" k="labs_qualified" sort={sort} num /></th>
              <th className="num"><SortHeader label="Labs totales" k="labs_total" sort={sort} num /></th>
              <th className="num"><SortHeader label="Productos" k="products" sort={sort} num /></th>
              <th className="num"><SortHeader label="Ventas anuales" k="sales_annual" sort={sort} num /></th>
              <th className="num"><SortHeader label="V/S" k="ratio_vs" sort={sort} num /></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sort.sortedRows.length === 0 && (
              <tr><td colSpan={8} className="text-center py-6 text-slate-500">Sin datos de parafarmacia.</td></tr>
            )}
            {sort.sortedRows.map((f, i) => {
              const empty = f.labs_qualified === 0;
              return (
                <tr
                  key={f.familia}
                  onClick={() => open(f.familia)}
                  className="cursor-pointer hover:bg-blue-50"
                  data-testid={`family-coverage-row-${f.familia}`}
                >
                  <td className="font-mono text-slate-400">{i + 1}</td>
                  <td className="font-semibold">{f.familia}</td>
                  <td className={"num font-bold " + (empty ? "text-slate-400" : "text-blue-700")}>{f.labs_qualified}</td>
                  <td className="num text-slate-500">{f.labs_total}</td>
                  <td className="num">{formatNumber(f.products)}</td>
                  <td className="num">{formatMoney(f.sales_annual)}</td>
                  <td className={"num font-bold " + ((f.ratio_vs || 0) >= 1 ? "text-emerald-700" : "text-red-600")}>
                    {(f.ratio_vs || 0).toFixed(2)}×
                  </td>
                  <td className="text-blue-700 text-xs font-semibold whitespace-nowrap">{empty ? <span className="text-slate-400">—</span> : (
                    <Link to={`/labs?segment=parafarmacia&familia=${encodeURIComponent(f.familia)}`} onClick={(e) => e.stopPropagation()} className="hover:underline">Comparar labs →</Link>)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <DrillDown open={!!famDrill} onClose={() => setFamDrill(null)} studyId={studyId} title={`${famDrill || ""} · Parafarmacia`}
                 subtitle="Desglose de la familia" filters={{ familia: famDrill, segment: "parafarmacia" }} />
    </div>
  );
}

function RecentStudiesTable({ studies }) {
  const recent = studies.slice(0, 5);
  const sort = useSortable(recent, "created_at", "desc");
  return (
    <div className="card p-6 mt-8">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="font-display text-lg font-bold">Mis estudios</h3>
        <Link to="/studies" className="text-sm font-semibold text-slate-900 hover:underline">Ver todos →</Link>
      </div>
      <div className="overflow-x-auto">
      <table className="dense">
        <thead>
          <tr>
            <th><SortHeader label="Nombre" k="name" sort={sort} defaultDir="asc" /></th>
            <th><SortHeader label="Periodo" k="period_days" sort={sort} num /></th>
            <th className="num"><SortHeader label="Productos" k="total_products" sort={sort} num /></th>
            <th className="num"><SortHeader label="Stock" k="total_stock_value" sort={sort} num /></th>
            <th className="num"><SortHeader label="Ventas" k="total_sales_value" sort={sort} num /></th>
            <th><SortHeader label="Fecha" k="created_at" sort={sort} /></th>
          </tr>
        </thead>
        <tbody>
          {sort.sortedRows.map((s) => (
            <tr key={s.id}>
              <td><Link to={`/studies/${s.id}`} className="font-semibold hover:underline">{s.name}</Link></td>
              <td className="font-mono">{s.period_days}d</td>
              <td className="num">{formatNumber(s.total_products)}</td>
              <td className="num">{formatMoney(s.total_stock_value)}</td>
              <td className="num">{formatMoney(s.total_sales_value)}</td>
              <td>{new Date(s.created_at).toLocaleDateString("es-ES")}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

const HIGH_ROT = ["AA", "A", "BB", "B"];
const LOW_ROT = ["CC", "C", "D"];

function EfficiencyPanel({ latest, compact = false, onClassClick }) {
  if (!latest) return null;
  const t = latest.totals;
  const totals = latest.abcd_totals || {};
  const ts = t.total_sales_value || 1;
  const tk = t.total_stock_value || 1;

  // Per-class %sales and %stock
  const rows = ABCD_ORDER.map((k) => {
    const ventas = totals[k]?.sales_value || 0;
    const stock = totals[k]?.stock_value || 0;
    const productos = latest.abcd_counts?.[k] || 0;
    return {
      code: k,
      label: ABCD_LABELS[k],
      ventas,
      stock,
      pct_ventas: ventas / ts,
      pct_stock: stock / tk,
      delta_pct: (ventas / ts) - (stock / tk),
      productos,
    };
  });

  // Aggregates
  const aggregate = (codes) => {
    let v = 0, s = 0, p = 0;
    codes.forEach((k) => {
      v += totals[k]?.sales_value || 0;
      s += totals[k]?.stock_value || 0;
      p += latest.abcd_counts?.[k] || 0;
    });
    return { ventas: v, stock: s, pct_ventas: v / ts, pct_stock: s / tk, productos: p };
  };
  const alta = aggregate(HIGH_ROT);
  const baja = aggregate(LOW_ROT);

  // Chart data: side-by-side bars per class
  const chartData = rows.map((r) => ({
    code: r.code,
    label: ABCD_LABELS[r.code],
    "% Ventas": +(r.pct_ventas * 100).toFixed(2),
    "% Stock": +(r.pct_stock * 100).toFixed(2),
  }));

  return (
    <div className={(compact ? "fade-in" : "card p-6 mb-8 fade-in")} data-testid="efficiency-panel">
      {!compact && (
        <div className="flex items-baseline justify-between mb-1 flex-wrap gap-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Eficiencia</div>
            <h3 className="font-display text-xl font-bold mt-1">Ventas vs Stock por clasificación</h3>
          </div>
          <div className="text-xs text-slate-500 italic max-w-md text-right">
            Alta rotación (AA·A·BB·B) deberían aportar muchas ventas con poco stock. Si % stock ≥ % ventas → exceso de inmovilizado.
          </div>
        </div>
      )}
      {compact && (
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Eficiencia · Ventas vs Stock</div>
      )}

      {/* Two big aggregate KPIs */}
      <div className={"grid gap-4 " + (compact ? "grid-cols-1 sm:grid-cols-2 mb-4" : "grid-cols-1 md:grid-cols-2 mt-5 mb-6")}>
        <EfficiencyBigKpi
          title="Alta rotación"
          codes={HIGH_ROT}
          data={alta}
          variant="positive"
          testid="kpi-alta-rotacion"
        />
        <EfficiencyBigKpi
          title="Baja rotación"
          codes={LOW_ROT}
          data={baja}
          variant="negative"
          testid="kpi-baja-rotacion"
        />
      </div>

      {/* 100% stacked horizontal bars (visual side-by-side comparison) */}
      <div className={compact ? "mb-4" : "mb-6"}>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Distribución comparada (100%)</div>
        <StackedRow label="VENTAS" rows={rows} keyName="pct_ventas" totalLabel={formatMoney(t.total_sales_value)} />
        <div className="h-2" />
        <StackedRow label="STOCK" rows={rows} keyName="pct_stock" totalLabel={formatMoney(t.total_stock_value)} />
        <div className="flex gap-3 mt-3 flex-wrap text-[10px]">
          {ABCD_ORDER.map((k) => (
            <div key={k} className="flex items-center gap-1">
              <div className="h-2.5 w-2.5 rounded-sm" style={{ background: ABCD_COLORS[k] }} />
              <span className="font-mono font-semibold">{k}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Side-by-side bars per class — solo en versión completa */}
      {!compact && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">% Ventas vs % Stock por clase</div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} margin={{ top: 10, right: 0, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="code" tick={{ fontSize: 12, fontWeight: 600 }} />
                <YAxis tick={{ fontSize: 11 }} unit="%" />
                <Tooltip formatter={(v) => `${v}%`} contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="% Ventas" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="% Stock" fill="#0F172A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Diagnóstico por clase</div>
            <table className="dense" data-testid="efficiency-table">
              <thead>
                <tr><th>Clase</th><th className="num">% Vts</th><th className="num">% Stk</th><th>Estado</th></tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const ok = r.delta_pct >= 0;
                  const clickable = !!onClassClick;
                  return (
                    <tr
                      key={r.code}
                      onClick={clickable ? () => onClassClick(r.code) : undefined}
                      className={clickable ? "cursor-pointer hover:bg-slate-50" : undefined}
                      data-testid={`efficiency-row-${r.code}`}
                    >
                      <td><span className={`abcd-pill abcd-${r.code}`}>{r.code}</span></td>
                      <td className="num">{formatPct(r.pct_ventas)}</td>
                      <td className="num">{formatPct(r.pct_stock)}</td>
                      <td>
                        {r.pct_ventas === 0 && r.pct_stock === 0 ? (
                          <span className="text-slate-400 text-[11px]">—</span>
                        ) : ok ? (
                          <span className="badge-perfecto">OK</span>
                        ) : (
                          <span className="badge-mejorar">EXCESO</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Compact: tabla diagnóstico únicamente */}
      {compact && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Diagnóstico por clase</div>
          <table className="dense" data-testid="efficiency-table">
            <thead>
              <tr><th>Clase</th><th className="num">% Vts</th><th className="num">% Stk</th><th>Estado</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const ok = r.delta_pct >= 0;
                const clickable = !!onClassClick;
                return (
                  <tr
                    key={r.code}
                    onClick={clickable ? () => onClassClick(r.code) : undefined}
                    className={clickable ? "cursor-pointer hover:bg-slate-50" : undefined}
                    data-testid={`efficiency-row-c-${r.code}`}
                  >
                    <td><span className={`abcd-pill abcd-${r.code}`}>{r.code}</span></td>
                    <td className="num">{formatPct(r.pct_ventas)}</td>
                    <td className="num">{formatPct(r.pct_stock)}</td>
                    <td>
                      {r.pct_ventas === 0 && r.pct_stock === 0 ? (
                        <span className="text-slate-400 text-[11px]">—</span>
                      ) : ok ? (
                        <span className="badge-perfecto">OK</span>
                      ) : (
                        <span className="badge-mejorar">EXCESO</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EfficiencyBigKpi({ title, codes, data, variant, testid }) {
  const ok = (variant === "positive" && data.pct_ventas >= data.pct_stock) ||
             (variant === "negative" && data.pct_ventas <= data.pct_stock);
  const verdict = variant === "positive"
    ? (ok ? "Buen ratio — venden más de lo que ocupan" : "Atención — ocupan más stock del que aportan en ventas")
    : (ok ? "Buen ratio — ocupan poco stock para lo que aportan" : "Mucho inmovilizado para tan pocas ventas — revisar");
  const accent = ok ? "border-emerald-500" : "border-red-500";
  return (
    <div className={`border-l-4 ${accent} pl-4 py-2`} data-testid={testid}>
      <div className="flex items-baseline justify-between">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{title}</div>
        <div className="font-mono text-[11px] text-slate-500">{codes.join(" · ")}</div>
      </div>
      <div className="grid grid-cols-2 gap-4 mt-2">
        <div>
          <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-widest">% Ventas</div>
          <div className="font-display text-3xl font-extrabold text-emerald-600">{formatPct(data.pct_ventas)}</div>
          <div className="text-[11px] text-slate-500">{formatMoney(data.ventas)}</div>
        </div>
        <div>
          <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-widest">% Stock</div>
          <div className="font-display text-3xl font-extrabold text-slate-900">{formatPct(data.pct_stock)}</div>
          <div className="text-[11px] text-slate-500">{formatMoney(data.stock)}</div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 text-xs">
        <span className={"px-2 py-0.5 rounded text-[11px] font-bold " + (ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700")}>
          Δ {(data.pct_ventas - data.pct_stock >= 0 ? "+" : "")}{((data.pct_ventas - data.pct_stock) * 100).toFixed(1)} pp
        </span>
        <span className="text-slate-600">{verdict}</span>
      </div>
    </div>
  );
}

function AlertsBanner({ latest }) {
  if (!latest) return null;
  const totals = latest.abcd_totals || {};
  const counts = latest.abcd_counts || {};
  const ts = latest.totals.total_sales_value || 1;
  const tk = latest.totals.total_stock_value || 1;

  const sumKey = (codes, field) => codes.reduce((a, k) => a + (totals[k]?.[field] || 0), 0);
  const altaV = sumKey(["AA", "A", "BB", "B"], "sales_value") / ts;
  const altaS = sumKey(["AA", "A", "BB", "B"], "stock_value") / tk;
  const bajaS = sumKey(["CC", "C", "D"], "stock_value") / tk;

  const alerts = [];
  // Anomalía operativa: alta rotación con menos % ventas que % stock
  if (altaV < altaS) {
    alerts.push({
      level: "danger",
      title: "⚠ Anomalía operativa: alta rotación sub-aporta",
      msg: `Tu alta rotación (AA+A+BB+B) aporta el ${(altaV*100).toFixed(1)}% de las ventas pero ocupa el ${(altaS*100).toFixed(1)}% del stock. Esperado: ventas > stock. Revisa exceso de pedido o pérdida de rotación.`,
    });
  }
  // Stock D Muerto importante
  const dMuerto = latest.stock_d_subdivision?.d_muerto;
  if (dMuerto && dMuerto.count > 0 && dMuerto.stock_value > tk * 0.03) {
    alerts.push({
      level: "warn",
      title: `🔴 Stock D Muerto: ${formatNumber(dMuerto.count)} productos = ${formatMoney(dMuerto.stock_value)}`,
      msg: `Productos sin venta con última entrada >1 año o sin fecha de entrada (el D real, solo con fecha, está en Resumen general · Stock parado). Inmovilizado el ${((dMuerto.stock_value/tk)*100).toFixed(1)}% del valor del stock. Candidatos a devolución / outlet.`,
    });
  }
  // Baja rotación con demasiado stock
  if (bajaS > 0.5) {
    alerts.push({
      level: "warn",
      title: `📦 Exceso de inmovilizado en baja rotación: ${(bajaS*100).toFixed(1)}%`,
      msg: `Las clases CC+C+D ocupan más del 50% del stock. Reduce compras de estas clases y libera capital.`,
    });
  }
  // CC con más stock que ventas en proporción 2:1
  const ccV = (totals.CC?.sales_value || 0) / ts;
  const ccS = (totals.CC?.stock_value || 0) / tk;
  if (ccS > ccV * 1.8 && ccS > 0.05) {
    alerts.push({
      level: "warn",
      title: "📉 Clase CC sobre-stockeada",
      msg: `${(ccS*100).toFixed(1)}% del stock vs ${(ccV*100).toFixed(1)}% de ventas. Productos trimestrales con stock para meses; ajusta pedidos.`,
    });
  }
  // Positivo: todo bien
  if (alerts.length === 0) {
    alerts.push({
      level: "ok",
      title: "✅ Estructura de stock equilibrada",
      msg: `Alta rotación 80%+ de ventas con menos % stock. Sigue así.`,
    });
  }

  return (
    <div className="space-y-2 mb-6" data-testid="alerts-banner">
      {alerts.map((a, i) => {
        const Icon = a.level === "ok" ? CheckCircle2 : a.level === "danger" ? AlertCircle : AlertTriangle;
        const colors = a.level === "ok"
          ? "border-emerald-500 bg-emerald-50"
          : a.level === "danger" ? "border-red-500 bg-red-50" : "border-amber-500 bg-amber-50";
        const iconC = a.level === "ok" ? "text-emerald-600" : a.level === "danger" ? "text-red-600" : "text-amber-600";
        return (
          <div key={i} className={"flex gap-3 p-3 rounded-lg border-l-4 " + colors} data-testid={`alert-${i}`}>
            <Icon size={18} className={iconC + " mt-0.5 shrink-0"} />
            <div className="text-sm">
              <div className="font-semibold">{a.title}</div>
              <div className="text-slate-700">{a.msg}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StackedRow({ label, rows, keyName, totalLabel }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-xs font-mono font-bold text-slate-700 tracking-widest">{label}</div>
        <div className="text-xs text-slate-500">{totalLabel}</div>
      </div>
      <div className="flex h-9 w-full rounded overflow-hidden border border-slate-200" data-testid={`stacked-${label.toLowerCase()}`}>
        {rows.map((r) => {
          const pct = (r[keyName] || 0) * 100;
          if (pct < 0.1) return null;
          return (
            <div
              key={r.code}
              className="flex items-center justify-center text-[10px] font-mono font-bold text-white"
              style={{ width: `${pct}%`, background: ABCD_COLORS[r.code], minWidth: 0 }}
              title={`${r.code} ${ABCD_LABELS[r.code]} · ${pct.toFixed(1)}%`}
            >
              {pct >= 4 && <span>{r.code} {pct.toFixed(0)}%</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function TopLabsCards({ data, studyId, segment }) {
  const navigate = useNavigate();
  const bestSort = useSortable(data?.top_best || [], "ratio_ventas_stock", "desc");
  const worstSort = useSortable(data?.top_worst || [], "ratio_ventas_stock", "asc");
  if (!data) return null;
  const open = (lab) => {
    const params = new URLSearchParams({ tab: "laboratorios", lab });
    if (segment) params.set("segment", segment);
    navigate(`/studies/${studyId}?${params.toString()}`);
  };
  return (
    <div>
    <div className="flex justify-end mb-2">
      <ExportButtons filename={`ranking-laboratorios${segment ? "-" + segment : ""}`} sheetName="Laboratorios" testIdPrefix={`top-labs-export${segment ? "-" + segment : ""}`}
                     title={`Ranking de laboratorios por eficiencia${segment ? " · " + (segment === "farmacia" ? "Farmacia" : "Parafarmacia") : ""}`}
                     rows={[...(data.labs || [...(data.top_best || []), ...(data.top_worst || [])])].sort((a, b) => (b.ratio_ventas_stock || 0) - (a.ratio_ventas_stock || 0))}
                     columns={[col.text("Laboratorio", "laboratorio"), col.eur("Ventas", "sales"), col.eur("Stock", "stock_value"), col.num("V/S", "ratio_ventas_stock")]} />
    </div>
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <div className="card p-5 min-w-0">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-7 w-7 rounded-lg bg-emerald-100 flex items-center justify-center">
            <ArrowUp size={13} className="text-emerald-600" />
          </div>
          <h4 className="font-display text-sm font-bold">Top 5 más eficientes</h4>
        </div>
        <div className="overflow-x-auto">
        <table className="dense" data-testid={`top-best-labs${segment ? '-' + segment : ''}`}>
          <thead>
            <tr>
              <th>#</th>
              <th><SortHeader label="Laboratorio" k="laboratorio" sort={bestSort} defaultDir="asc" /></th>
              <th className="num"><SortHeader label="Ventas" k="sales" sort={bestSort} num /></th>
              <th className="num"><SortHeader label="Stock" k="stock_value" sort={bestSort} num /></th>
              <th className="num"><SortHeader label="V/S" k="ratio_ventas_stock" sort={bestSort} num /></th>
            </tr>
          </thead>
          <tbody>
            {bestSort.sortedRows.map((l, i) => (
              <tr key={l.laboratorio} className="cursor-pointer hover:bg-emerald-50" onClick={() => open(l.laboratorio)} data-testid={`best-lab${segment ? '-' + segment : ''}-${i}`}>
                <td className="font-mono">{i + 1}</td>
                <td className="font-semibold">{l.laboratorio}</td>
                <td className="num">{formatMoney(l.sales)}</td>
                <td className="num text-slate-500">{formatMoney(l.stock_value)}</td>
                <td className="num text-emerald-700 font-bold">{(l.ratio_ventas_stock || 0).toFixed(2)}×</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      <div className="card p-5 min-w-0">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-7 w-7 rounded-lg bg-red-100 flex items-center justify-center">
            <ArrowDown size={13} className="text-red-600" />
          </div>
          <h4 className="font-display text-sm font-bold">Top 5 menos eficientes</h4>
        </div>
        <div className="overflow-x-auto">
        <table className="dense" data-testid={`top-worst-labs${segment ? '-' + segment : ''}`}>
          <thead>
            <tr>
              <th>#</th>
              <th><SortHeader label="Laboratorio" k="laboratorio" sort={worstSort} defaultDir="asc" /></th>
              <th className="num"><SortHeader label="Ventas" k="sales" sort={worstSort} num /></th>
              <th className="num"><SortHeader label="Stock" k="stock_value" sort={worstSort} num /></th>
              <th className="num"><SortHeader label="V/S" k="ratio_ventas_stock" sort={worstSort} num /></th>
            </tr>
          </thead>
          <tbody>
            {worstSort.sortedRows.map((l, i) => (
              <tr key={l.laboratorio} className="cursor-pointer hover:bg-red-50" onClick={() => open(l.laboratorio)} data-testid={`worst-lab${segment ? '-' + segment : ''}-${i}`}>
                <td className="font-mono">{i + 1}</td>
                <td className="font-semibold">{l.laboratorio}</td>
                <td className="num">{formatMoney(l.sales)}</td>
                <td className="num text-slate-500">{formatMoney(l.stock_value)}</td>
                <td className="num text-red-700 font-bold">{(l.ratio_ventas_stock || 0).toFixed(2)}×</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
    </div>
  );
}

function TopLabsPanel({ studyId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!studyId) return;
    (async () => {
      try {
        const { data: d } = await api.get(`/studies/${studyId}/labs-margin`);
        setData(d);
      } finally { setLoading(false); }
    })();
  }, [studyId]);

  if (loading) return <div className="card p-6 mb-8 text-slate-500"><Loader2 size={14} className="animate-spin inline mr-2" />Cargando ranking de laboratorios…</div>;
  if (!data) return null;

  return (
    <div className="mb-8" data-testid="top-labs-panel">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Eficiencia comercial</div>
          <h3 className="font-display text-xl font-bold mt-1">Ranking de laboratorios — ventas por € de stock</h3>
        </div>
        <div className="text-xs text-slate-500 italic max-w-md text-right">
          Mide cuántos € de ventas genera cada € de stock inmovilizado. Click → desglose ABCD.
        </div>
      </div>
      <TopLabsCards data={data} studyId={studyId} />
    </div>
  );
}

const SEGMENT_META = {
  farmacia: {
    label: "FARMACIA",
    sub: "CN ≥ 400.000 · medicamento",
    accent: "border-sky-500",
    badge: "bg-sky-100 text-sky-800",
    icon: "Rx",
  },
  parafarmacia: {
    label: "PARAFARMACIA",
    sub: "CN < 400.000 · libre venta",
    accent: "border-violet-500",
    badge: "bg-violet-100 text-violet-800",
    icon: "OTC",
  },
};

function SegmentColumn({ segment, data, productsCount, studyId }) {
  const meta = SEGMENT_META[segment];
  const t = data.totals;
  const dMuerto = data.stock_d_subdivision?.d_muerto || { count: 0, stock_value: 0 };
  const navigate = useNavigate();
  // Build a "latest"-shaped object so we can reuse AlertsBanner/EfficiencyPanel
  const latestShape = {
    totals: t,
    abcd_totals: data.abcd_totals,
    abcd_counts: data.abcd_counts,
    stock_d_subdivision: data.stock_d_subdivision,
  };
  const goDmuerto = () => navigate(`/studies/${studyId}?tab=stockd&segment=${segment}`);
  const onClassClick = (code) => navigate(`/studies/${studyId}?tab=ventas&segment=${segment}&abcd=${code}`);
  return (
    <div className={`border-t-4 ${meta.accent} bg-white rounded-lg shadow-sm`} data-testid={`segment-${segment}`}>
      <div className="px-5 pt-4 pb-3 flex items-center gap-3 border-b border-slate-200">
        <span className={`text-[10px] font-mono font-bold px-2 py-1 rounded ${meta.badge}`}>{meta.icon}</span>
        <div className="flex-1">
          <h3 className="font-display text-lg font-extrabold tracking-tight">{meta.label}</h3>
          <p className="text-[11px] text-slate-500">{meta.sub} · {formatNumber(productsCount)} productos</p>
        </div>
      </div>

      <div className="px-5 py-4 space-y-5">
        {/* Mini KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid={`segment-kpis-${segment}`}>
          <div>
            <div className="kpi-label">Stock (PVP)</div>
            <div className="font-display text-xl font-bold">{formatMoney(t.total_stock_value)}</div>
            <div className="kpi-sub">{formatNumber(t.total_stock_units)} uds</div>
          </div>
          <div>
            <div className="kpi-label">Ventas</div>
            <div className="font-display text-xl font-bold">{formatMoney(t.total_sales_value)}</div>
            <div className="kpi-sub">{formatNumber(t.total_units)} uds</div>
          </div>
          <div>
            <div className="kpi-label">Rotación</div>
            <div className="font-display text-xl font-bold">{(t.rotacion_global || 0).toFixed(2).replace(".", ",")}×</div>
            <div className="kpi-sub">ventas PVP / stock PVP</div>
          </div>
          <button
            type="button"
            onClick={goDmuerto}
            className="text-left cursor-pointer rounded hover:bg-red-50 transition px-1 -mx-1"
            data-testid={`segment-d-muerto-${segment}`}
          >
            <div className="kpi-label flex items-center gap-1">D Muerto <InfoD term="muerto" /></div>
            <div className="font-display text-xl font-bold text-red-600">{formatMoney(dMuerto.stock_value)}</div>
            <div className="kpi-sub">{dMuerto.count} prods · <span className="text-red-600 font-semibold">ver →</span></div>
          </button>
        </div>

        {/* Alertas inteligentes del segmento */}
        <AlertsBanner latest={latestShape} />

        {/* Top labs */}
        <TopLabsCards data={data.labs_margin} studyId={studyId} segment={segment} />

        {/* Efficiency panel — class rows clickable */}
        <EfficiencyPanel latest={latestShape} compact onClassClick={onClassClick} />
      </div>
    </div>
  );
}

function SegmentedView({ studyId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!studyId) return;
    (async () => {
      try {
        const { data: d } = await api.get(`/studies/${studyId}/segmented`);
        setData(d);
      } finally { setLoading(false); }
    })();
  }, [studyId]);

  if (loading) return <div className="card p-6 mb-8 text-slate-500"><Loader2 size={14} className="animate-spin inline mr-2" />Calculando segmentación Farmacia / Parafarmacia…</div>;
  if (!data) return null;

  return (
    <div className="mb-8" data-testid="segmented-view">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Segmentación por canal</div>
          <h3 className="font-display text-xl font-bold mt-1">Farmacia · Parafarmacia</h3>
        </div>
        <div className="text-xs text-slate-500 italic max-w-lg text-right">
          Clasificación por código nacional: <b>parafarmacia</b> CN &lt; 400.000 · <b>farmacia</b> CN ≥ 400.000.
          {data.counts.sin_cn_numerico > 0 && (
            <span> · {data.counts.sin_cn_numerico} productos sin CN numérico excluidos</span>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <SegmentColumn segment="farmacia" data={data.farmacia} productsCount={data.counts.farmacia} studyId={studyId} />
        <SegmentColumn segment="parafarmacia" data={data.parafarmacia} productsCount={data.counts.parafarmacia} studyId={studyId} />
      </div>
    </div>
  );
}

